import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

interface SwitchToCliDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  loading: boolean;
}

const CONFIRM_PHRASE = 'HookMyApp CLI';

export function SwitchToCliDialog({
  open,
  onOpenChange,
  onConfirm,
  loading,
}: SwitchToCliDialogProps) {
  const [confirmText, setConfirmText] = useState('');

  const confirmed = confirmText === CONFIRM_PHRASE;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!loading) {
          onOpenChange(nextOpen);
          if (!nextOpen) setConfirmText('');
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Switch back to HookMyApp CLI?</AlertDialogTitle>
          <AlertDialogDescription>
            Switching will stop delivering events to your webhook URL.
            Inbound events will route to the HookMyApp CLI instead. Make
            sure the CLI is running, or events may be missed.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-2 px-0">
          <label className="text-sm font-medium">
            Type &ldquo;{CONFIRM_PHRASE}&rdquo; to confirm:
          </label>
          <Input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type ${CONFIRM_PHRASE} to confirm`}
            aria-label="Type HookMyApp CLI to confirm switch"
            autoComplete="off"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Keep webhook URL</AlertDialogCancel>
          <AlertDialogAction
            disabled={!confirmed || loading}
            onClick={onConfirm}
          >
            {loading && <Loader2 className="size-3.5 animate-spin" />}
            {loading ? 'Switching...' : 'Switch to CLI'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
